import { useMutation } from "@tanstack/react-query";
import { CheckIcon, ClipboardPlusIcon, SendIcon } from "lucide-react";
import { useId, useState, type SyntheticEvent } from "react";

import { ToolRequestSubmissionSchema, type ToolRequestCreated } from "../../shared/schemas";
import { ApiClientError, submitToolRequest } from "../api/client";
import { Alert, AlertDescription } from "./ui/alert";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from "./ui/field";
import { Textarea } from "./ui/textarea";

type FormErrors = {
  systems?: string;
  whatItDoes?: string;
};

function parseFormErrors(issues: { path: PropertyKey[]; message: string }[]): FormErrors {
  const errors: FormErrors = {};
  for (const issue of issues) {
    const field = issue.path[0];
    if (field === "systems" && !errors.systems) errors.systems = issue.message;
    if (field === "whatItDoes" && !errors.whatItDoes) errors.whatItDoes = issue.message;
  }
  return errors;
}

function useToolRequest(organizationId: string) {
  const [systems, setSystems] = useState("");
  const [whatItDoes, setWhatItDoes] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});
  const [created, setCreated] = useState<ToolRequestCreated | null>(null);
  const request = useMutation({ mutationFn: submitToolRequest });
  function reset() {
    request.reset();
    setCreated(null);
    setErrors({});
    setSystems("");
    setWhatItDoes("");
  }
  function submit(event: SyntheticEvent<HTMLFormElement, SubmitEvent>) {
    event.preventDefault();
    const parsed = ToolRequestSubmissionSchema.safeParse({ organizationId, systems, whatItDoes });
    if (!parsed.success) {
      setErrors(parseFormErrors(parsed.error.issues));
      return;
    }
    setErrors({});
    request.mutate(parsed.data, { onSuccess: setCreated });
  }
  const serverError =
    request.error instanceof ApiClientError
      ? request.error.message
      : request.isError
        ? "The request could not be sent. Check your connection and try again."
        : null;
  return {
    created,
    errors,
    request,
    reset,
    serverError,
    setErrors,
    setSystems,
    setWhatItDoes,
    submit,
    systems,
    whatItDoes,
  };
}

type ToolRequestController = ReturnType<typeof useToolRequest>;

function RequestSent() {
  return (
    <>
      <DialogHeader className="justify-items-center pr-0 text-center">
        <div className="mb-2 grid size-12 place-items-center rounded-sm border border-success bg-success-background text-success">
          <CheckIcon aria-hidden="true" className="size-5" />
        </div>
        <DialogTitle>Request sent</DialogTitle>
        <DialogDescription className="max-w-sm">
          Your consultant will be in touch soon to talk through the workflow and next steps.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter className="justify-center border-0 pt-0 sm:justify-center">
        <DialogClose render={<Button type="button" />}>Done</DialogClose>
      </DialogFooter>
    </>
  );
}

function RequestFields({ controller }: { controller: ToolRequestController }) {
  const whatItDoesId = useId();
  const systemsId = useId();
  const clearError = (field: keyof FormErrors) => {
    controller.setErrors((current) => ({ ...current, [field]: "" }));
  };
  return (
    <FieldGroup>
      <Field data-invalid={Boolean(controller.errors.whatItDoes)}>
        <FieldLabel htmlFor={whatItDoesId}>What does it need to do?</FieldLabel>
        <Textarea
          aria-describedby={`${whatItDoesId}-help${controller.errors.whatItDoes ? ` ${whatItDoesId}-error` : ""}`}
          aria-invalid={Boolean(controller.errors.whatItDoes)}
          autoComplete="off"
          disabled={controller.request.isPending}
          id={whatItDoesId}
          maxLength={4000}
          name="whatItDoes"
          placeholder="Describe the workflow, who uses it, and what a good result looks like."
          value={controller.whatItDoes}
          onChange={(event) => {
            controller.setWhatItDoes(event.target.value);
            clearError("whatItDoes");
          }}
        />
        <FieldDescription id={`${whatItDoesId}-help`}>
          Include the current process and the part that wastes the most time.
        </FieldDescription>
        <FieldError id={`${whatItDoesId}-error`}>{controller.errors.whatItDoes}</FieldError>
      </Field>
      <Field data-invalid={Boolean(controller.errors.systems)}>
        <FieldLabel htmlFor={systemsId}>What systems does it need to integrate with?</FieldLabel>
        <Textarea
          aria-describedby={`${systemsId}-help${controller.errors.systems ? ` ${systemsId}-error` : ""}`}
          aria-invalid={Boolean(controller.errors.systems)}
          autoComplete="off"
          className="min-h-24"
          disabled={controller.request.isPending}
          id={systemsId}
          maxLength={1000}
          name="systems"
          placeholder="QuickBooks, HubSpot, an existing database, CSV files—or none."
          value={controller.systems}
          onChange={(event) => {
            controller.setSystems(event.target.value);
            clearError("systems");
          }}
        />
        <FieldDescription id={`${systemsId}-help`}>
          Name any software, files, devices, or APIs involved.
        </FieldDescription>
        <FieldError id={`${systemsId}-error`}>{controller.errors.systems}</FieldError>
      </Field>
    </FieldGroup>
  );
}

function RequestForm({
  controller,
  isOnline,
  organizationName,
}: {
  controller: ToolRequestController;
  isOnline: boolean;
  organizationName: string | undefined;
}) {
  return (
    <form className="grid gap-5" noValidate onSubmit={controller.submit}>
      <DialogHeader>
        <DialogTitle>Request a tool</DialogTitle>
        <DialogDescription>
          Tell us what your team needs in {organizationName ?? "this workspace"}. Your consultant
          will review the request with you.
        </DialogDescription>
      </DialogHeader>
      <RequestFields controller={controller} />
      {!isOnline ? (
        <Alert role="status">
          <AlertDescription>
            Internet connection required. Reconnect before sending this request.
          </AlertDescription>
        </Alert>
      ) : null}
      {controller.serverError ? (
        <Alert role="alert" variant="destructive">
          <AlertDescription>{controller.serverError}</AlertDescription>
        </Alert>
      ) : null}
      <DialogFooter>
        <DialogClose
          disabled={controller.request.isPending}
          render={<Button type="button" variant="ghost" />}
        >
          Cancel
        </DialogClose>
        <Button disabled={!isOnline || controller.request.isPending} type="submit">
          <SendIcon aria-hidden="true" data-icon="inline-start" />
          {controller.request.isPending ? "Sending request…" : "Send request"}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function RequestToolDialog({
  isOnline,
  organizationId,
  organizationName,
}: {
  isOnline: boolean;
  organizationId: string;
  organizationName: string | undefined;
}) {
  const [open, setOpen] = useState(false);
  const controller = useToolRequest(organizationId);

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen && controller.request.isPending) return;
    if (nextOpen && !open) controller.reset();
    setOpen(nextOpen);
  }

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogTrigger
        render={
          <Button
            className="w-full sm:w-auto"
            disabled={!isOnline || !organizationId}
            type="button"
          />
        }
      >
        <ClipboardPlusIcon aria-hidden="true" data-icon="inline-start" />
        Request Tool
      </DialogTrigger>

      <DialogContent>
        {controller.created ? (
          <RequestSent />
        ) : (
          <RequestForm
            controller={controller}
            isOnline={isOnline}
            organizationName={organizationName}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
